import { useEffect, useMemo, useState } from 'react';
import { useOneButtonStore } from '../../../../store/oneButtonStore';
import { computeCommerceGuide, type CommerceGuideSection } from './commerceGuideLogic';

export type DashboardCollapsibleCardId = 'system-design' | 'financing' | 'partner-sync' | 'quotes';

/** Maps Sunny / commerce-guide focus to the collapsible dashboard card that should start open. */
export function guideSectionToFocusedCard(
    section: CommerceGuideSection,
): DashboardCollapsibleCardId | null {
    switch (section) {
        case 'financing':
            return 'financing';
        case 'partnerSync':
            return 'partner-sync';
        case 'products':
            return 'system-design';
        case 'quotes':
            /* Inline quotes accordion only on section panels; main “all” view uses commerce hub. */
            return 'partner-sync';
        case 'agreements':
        case 'changeOrder':
            return 'partner-sync';
        default:
            return null;
    }
}

/** Dashboard sections that start expanded on load (in addition to Sunny guide focus). */
const DEFAULT_EXPANDED_CARDS: ReadonlySet<DashboardCollapsibleCardId> = new Set([
    'system-design',
    'partner-sync',
]);

type UseSunnyFocusedSectionOpenOptions = {
    /** When false, uses `fallbackOpen` instead of guide-driven focus (e.g. pricing modal embed). */
    enabled?: boolean;
    fallbackOpen?: boolean;
    /** Dedicated section panels (e.g. System rail) — keep body visible; ignore collapse. */
    alwaysOpen?: boolean;
};

/**
 * Collapsible dashboard sections: collapsed by default except DEFAULT_EXPANDED_CARDS and the card Sunny's guide targets.
 * Re-syncs when opportunity or guide inputs change; clears manual override when focus moves.
 */
export function useSunnyFocusedSectionOpen(
    cardId: DashboardCollapsibleCardId,
    options: UseSunnyFocusedSectionOpenOptions = {},
): [boolean, React.Dispatch<React.SetStateAction<boolean>>] {
    const { enabled = true, fallbackOpen = true, alwaysOpen = false } = options;

    const noopSetSectionOpen: React.Dispatch<React.SetStateAction<boolean>> = () => {};

    const selectedFinanceCells = useOneButtonStore((s) => s.selectedFinanceCells);
    const financePartnerSyncStatus = useOneButtonStore((s) => s.financePartnerSyncStatus);
    const opportunityDetails = useOneButtonStore((s) => s.opportunityDetails);
    const dashboardQuotesSummaryLines = useOneButtonStore((s) => s.dashboardQuotesSummaryLines);
    const changeOrderDetails = useOneButtonStore((s) => s.changeOrderDetails);
    const opportunityID = useOneButtonStore((s) => s.opportunityID);

    const opportunityKey = useMemo(() => {
        const raw =
            opportunityID ??
            opportunityDetails?.Opportunity_Id ??
            opportunityDetails?.OpportunityId ??
            null;
        return raw != null ? String(raw).trim() : '';
    }, [opportunityID, opportunityDetails]);

    const focusedCard = useMemo(() => {
        if (!enabled) return null;
        const { section } = computeCommerceGuide({
            selectedFinanceCells,
            financePartnerSyncStatus,
            opportunityDetails,
            dashboardQuotesSummaryLines,
            changeOrderDetails,
        });
        return guideSectionToFocusedCard(section);
    }, [
        enabled,
        selectedFinanceCells,
        financePartnerSyncStatus,
        opportunityDetails,
        dashboardQuotesSummaryLines?.selectedQuoteLine,
        dashboardQuotesSummaryLines?.signedContractLine,
        changeOrderDetails,
    ]);

    const guideWantsOpen = enabled
        ? DEFAULT_EXPANDED_CARDS.has(cardId) || focusedCard === cardId
        : fallbackOpen;

    const [userOverride, setUserOverride] = useState<boolean | null>(null);

    useEffect(() => {
        setUserOverride(null);
    }, [opportunityKey, focusedCard]);

    const sectionOpen = alwaysOpen ? true : userOverride ?? guideWantsOpen;

    const setSectionOpen: React.Dispatch<React.SetStateAction<boolean>> = (value) => {
        if (alwaysOpen) return;
        setUserOverride((prev) => {
            const current = prev ?? guideWantsOpen;
            const next = typeof value === 'function' ? value(current) : value;
            return next;
        });
    };

    if (alwaysOpen) {
        return [true, noopSetSectionOpen];
    }

    return [sectionOpen, setSectionOpen];
}
